import React, { useState, useEffect, useCallback, useMemo, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  AccountBalance as AccountBalanceIcon,
  LockOutlined as LockOutlinedIcon,
  Description as DescriptionIcon,
  RefreshRounded as RefreshRoundedIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import {
  tienePermisosModulo,
  puedeAccionProductos,
  puedeAccionFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, StatCard, fpDataGridSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import tiposDocumentosData from 'utils/tiposDocumentos.json';

/** Misma fila `programa` que la pestaña «Impuestos» en `MantenimientoProductos.jsx` */
const PROGRAMA_REGLAS_IMPUESTOS = 'reglasimpuestos';

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

const outlineBtnSx = {
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  borderRadius: 1.6,
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
};

const primaryBtnSx = {
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  boxShadow: 'none',
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const chipDocSx = { bgcolor: FP.color.infoSoft, color: FP.color.info, fontWeight: 600 };
const chipAplicarSubtotalSx = { bgcolor: FP.color.accentBlueSoft, color: FP.color.accentBlue, fontWeight: 600 };
const chipAplicarNetoSx = { bgcolor: FP.color.infoSoft, color: FP.color.info, fontWeight: 600 };

function parseDocumentosRegla(tipoDocumento) {
  let documentos = [];
  if (tipoDocumento) {
    try {
      documentos = JSON.parse(tipoDocumento);
      if (!Array.isArray(documentos)) documentos = [tipoDocumento];
    } catch {
      documentos = [tipoDocumento];
    }
  }
  return documentos;
}

function ReglasImpuestosToolbar() {
  return (
    <GridToolbarContainer sx={{ p: 1 }}>
      <GridToolbarExport
        sx={{
          color: FP.color.navy,
          textTransform: 'none',
          fontWeight: 700,
        }}
      />
    </GridToolbarContainer>
  );
}

const iconoTituloSx = {
  width: 30,
  height: 30,
  borderRadius: 1.2,
  bgcolor: FP.color.accentBlueSoft,
  color: FP.color.accentBlue,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const ReglasImpuestosVentas = forwardRef(function ReglasImpuestosVentas(
  {
    embedded = false,
    programaPermiso = PROGRAMA_REGLAS_IMPUESTOS,
    usarPermisoFacturacion = false,
  },
  ref
) {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, roleDetalles, usuarioLog, claveLog } = controller;

  const puedeLeer = tienePermisosModulo(roleDetalles, programaPermiso);
  const puedeModificar = usarPermisoFacturacion
    ? puedeAccionFacturacion(roleDetalles, programaPermiso, 'modificar')
    : puedeAccionProductos(roleDetalles, programaPermiso, 'modificar');
  const puedeEliminar = usarPermisoFacturacion
    ? puedeAccionFacturacion(roleDetalles, programaPermiso, 'borrar')
    : puedeAccionProductos(roleDetalles, programaPermiso, 'borrar');

  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const [reglasImpuestos, setReglasImpuestos] = useState([]);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [reglaEditando, setReglaEditando] = useState(null);
  const [cargando, setCargando] = useState(false);

  const [documentosAplicarImpuestos] = useState(() => {
    const documentos = [];
    tiposDocumentosData.forEach((categoria) => {
      if (categoria.documentosaplicarimpuestos && Array.isArray(categoria.documentosaplicarimpuestos)) {
        documentos.push(...categoria.documentosaplicarimpuestos);
      }
    });
    return documentos;
  });

  const [nombre, setNombre] = useState('');
  const [porcentaje, setPorcentaje] = useState('');
  const [aplicarSobre, setAplicarSobre] = useState('subtotal');
  const [tipoDocumento, setTipoDocumento] = useState([]);
  const [tipoOperacion, setTipoOperacion] = useState('sumar');
  const [montoBase, setMontoBase] = useState('0');
  const [orden, setOrden] = useState('0');
  const [activa, setActiva] = useState('S');
  const [cuentaCatalogo, setCuentaCatalogo] = useState(null);
  const [cuentasCatalogo, setCuentasCatalogo] = useState([]);
  const [busquedaCuenta, setBusquedaCuenta] = useState('');

  const cargarReglasImpuestos = useCallback(async () => {
    if (!selectedCompany?.db_empresa || !puedeLeer) return;
    setCargando(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerreglasimpuestos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setReglasImpuestos(data.reglas || []);
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al cargar reglas de impuestos');
      }
    } catch (error) {
      showError('Error de conexión al cargar reglas de impuestos');
    } finally {
      setCargando(false);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError, puedeLeer]);

  useEffect(() => {
    cargarReglasImpuestos();
  }, [cargarReglasImpuestos]);

  const buscarCuentasCatalogo = async (texto) => {
    if (!texto || texto.length < 2) {
      setCuentasCatalogo([]);
      return;
    }

    try {
      const response = await fetch(servidor + '/api/productosbackend_buscarcuentas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          busqueda: texto,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setCuentasCatalogo(data.cuentas || []);
      }
    } catch (error) {
      console.error('Error buscando cuentas', error);
    }
  };

  const buscarCuentaPorCodigo = async (codigo) => {
    if (!codigo) {
      setCuentaCatalogo(null);
      setBusquedaCuenta('');
      return;
    }

    try {
      const response = await fetch(servidor + '/api/productosbackend_buscarcuentas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          busqueda: codigo,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const cuentas = data.cuentas || [];
        const cuentaEncontrada = cuentas.find((c) => c.codigo === codigo);
        if (cuentaEncontrada) {
          setCuentaCatalogo(cuentaEncontrada);
          setBusquedaCuenta(cuentaEncontrada.texto || '');
        } else {
          setCuentaCatalogo({ codigo, texto: codigo, descripcion: codigo });
          setBusquedaCuenta(codigo);
        }
      }
    } catch (error) {
      console.error('Error buscando cuenta por código', error);
    }
  };

  const handleAbrirDialogo = (regla = null) => {
    if (!puedeModificar) {
      showError('No tiene permiso para crear o modificar reglas de impuestos.');
      return;
    }
    if (regla) {
      setModoEdicion(true);
      setReglaEditando(regla);
      setNombre(regla.nombre || '');
      setPorcentaje(regla.porcentaje?.toString() || '');
      setAplicarSobre(regla.aplicar_sobre || 'subtotal');
      let documentos = [];
      if (regla.tipo_documento) {
        try {
          documentos = JSON.parse(regla.tipo_documento);
          if (!Array.isArray(documentos)) {
            documentos = [regla.tipo_documento];
          }
        } catch (e) {
          documentos = [regla.tipo_documento];
        }
      }
      setTipoDocumento(documentos);
      setTipoOperacion(regla.tipo_operacion || 'sumar');
      setMontoBase(regla.monto_base?.toString() || '0');
      setOrden(regla.orden?.toString() || '0');
      setActiva(regla.activa || 'S');
      if (regla.cuenta_catalogo) {
        buscarCuentaPorCodigo(regla.cuenta_catalogo);
      } else {
        setCuentaCatalogo(null);
        setBusquedaCuenta('');
      }
    } else {
      setModoEdicion(false);
      setReglaEditando(null);
      setNombre('');
      setPorcentaje('');
      setAplicarSobre('subtotal');
      setTipoDocumento([]);
      setTipoOperacion('sumar');
      setMontoBase('0');
      setOrden('0');
      setActiva('S');
      setCuentaCatalogo(null);
      setBusquedaCuenta('');
    }
    setDialogoAbierto(true);
  };

  useImperativeHandle(ref, () => ({
    abrirNuevaRegla: () => handleAbrirDialogo(),
  }));

  const handleCerrarDialogo = () => {
    setDialogoAbierto(false);
    setModoEdicion(false);
    setReglaEditando(null);
    setNombre('');
    setPorcentaje('');
    setAplicarSobre('subtotal');
    setTipoDocumento([]);
    setTipoOperacion('sumar');
    setMontoBase('0');
    setOrden('0');
    setActiva('S');
    setCuentaCatalogo(null);
    setBusquedaCuenta('');
    setCuentasCatalogo([]);
  };

  const handleGuardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para guardar reglas de impuestos.');
      return;
    }
    if (!nombre.trim()) {
      showError('El nombre del impuesto es requerido');
      return;
    }

    if (!porcentaje || porcentaje === '') {
      showError('El porcentaje es requerido');
      return;
    }

    if (!tipoDocumento || tipoDocumento.length === 0) {
      showError('Debe seleccionar al menos un tipo de documento');
      return;
    }

    const porcentajeNum = parseFloat(porcentaje);
    if (isNaN(porcentajeNum) || porcentajeNum < 0 || porcentajeNum > 100) {
      showError('El porcentaje debe ser un número entre 0 y 100');
      return;
    }

    const montoBaseNum = parseFloat(montoBase);
    if (isNaN(montoBaseNum) || montoBaseNum < 0) {
      showError('El monto base debe ser un número mayor o igual a 0');
      return;
    }

    const ordenNum = parseInt(orden, 10);
    if (isNaN(ordenNum) || ordenNum < 0) {
      showError('El orden debe ser un número mayor o igual a 0');
      return;
    }

    try {
      const reglaData = {
        nombre: nombre.trim(),
        porcentaje: porcentajeNum,
        aplicar_sobre: aplicarSobre,
        tipo_documento: JSON.stringify(tipoDocumento),
        tipo_operacion: tipoOperacion,
        monto_base: montoBaseNum,
        orden: ordenNum,
        activa,
        cuenta_catalogo: cuentaCatalogo?.codigo || null,
      };

      if (modoEdicion && reglaEditando) {
        reglaData.id = reglaEditando.id;
      }

      const response = await fetch(servidor + '/api/facturabackend_guardarreglaimpuesto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          regla_impuesto: reglaData,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Regla de impuesto guardada exitosamente');
        handleCerrarDialogo();
        cargarReglasImpuestos();
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al guardar la regla de impuesto');
      }
    } catch (error) {
      showError('Error de conexión al guardar');
    }
  };

  const handleEliminar = async (regla) => {
    if (!puedeEliminar) {
      showError('No tiene permiso para eliminar reglas de impuestos.');
      return;
    }
    if (!window.confirm(`¿Está seguro de eliminar la regla de impuesto "${regla.nombre}"?`)) {
      return;
    }

    try {
      const response = await fetch(servidor + '/api/facturabackend_eliminarreglaimpuesto', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          regla_id: regla.id,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Regla de impuesto eliminada exitosamente');
        cargarReglasImpuestos();
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al eliminar la regla de impuesto');
      }
    } catch (error) {
      showError('Error de conexión al eliminar');
    }
  };

  const mostrarAccionesListado = puedeModificar || puedeEliminar;

  const reglasOrdenadas = useMemo(
    () => [...reglasImpuestos].sort((a, b) => (a.orden || 0) - (b.orden || 0)),
    [reglasImpuestos]
  );

  const totalActivas = useMemo(
    () => reglasImpuestos.filter((r) => r.activa === 'S').length,
    [reglasImpuestos]
  );

  const columnasReglasImpuestos = useMemo(() => {
    const cols = [
      {
        field: 'nombre',
        headerName: 'Nombre',
        flex: 1.15,
        minWidth: 168,
        renderCell: (params) => (
          <Typography sx={{ fontWeight: 700, fontSize: '0.875rem' }} noWrap title={params.value}>
            {params.value}
          </Typography>
        ),
      },
      {
        field: 'porcentaje',
        headerName: 'Porcentaje',
        flex: 0.42,
        minWidth: 100,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => (v != null && v !== '' ? `${Number(v).toFixed(2)}%` : ''),
      },
      {
        field: 'tipo_documento',
        headerName: 'Tipo de documento',
        flex: 1,
        minWidth: 188,
        sortable: false,
        renderCell: (params) => {
          const documentos = parseDocumentosRegla(params.value);
          if (documentos.length > 0) {
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', py: 0.25 }}>
                {documentos.map((doc, idx) => (
                  <Chip key={idx} label={doc} size="small" sx={chipDocSx} />
                ))}
              </Box>
            );
          }
          return <Chip label="—" size="small" variant="outlined" />;
        },
      },
      {
        field: 'aplicar_sobre',
        headerName: 'Aplicar sobre',
        flex: 0.65,
        minWidth: 140,
        renderCell: (params) => {
          const neto = params.value === 'subtotal_menos_descuentos';
          return (
            <Chip
              label={neto ? 'Subtotal − descuentos' : 'Subtotal'}
              size="small"
              sx={neto ? chipAplicarNetoSx : chipAplicarSubtotalSx}
            />
          );
        },
      },
      {
        field: 'tipo_operacion',
        headerName: 'Operación',
        flex: 0.4,
        minWidth: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const restar = params.value === 'restar';
          return (
            <Chip
              label={restar ? 'Restar' : 'Sumar'}
              size="small"
              sx={
                restar
                  ? { bgcolor: FP.color.dangerSoft, color: FP.color.danger, fontWeight: 600 }
                  : { bgcolor: FP.color.successSoft, color: FP.color.successDark, fontWeight: 600 }
              }
            />
          );
        },
      },
      {
        field: 'monto_base',
        headerName: 'Monto base',
        flex: 0.52,
        minWidth: 112,
        align: 'right',
        headerAlign: 'right',
        renderCell: (params) => {
          const v = params.row.monto_base;
          const txt =
            v != null
              ? Number(v).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
              : '0,00';
          return `$${txt}`;
        },
      },
      {
        field: 'orden',
        headerName: 'Orden',
        flex: 0.32,
        minWidth: 88,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => <Chip label={params.value ?? 0} size="small" variant="outlined" />,
      },
      {
        field: 'activa',
        headerName: 'Estado',
        flex: 0.42,
        minWidth: 100,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => {
          const on = params.value === 'S';
          return (
            <Chip
              label={on ? 'Activa' : 'Inactiva'}
              size="small"
              sx={
                on
                  ? { bgcolor: FP.color.successSoft, color: FP.color.successDark, fontWeight: 600 }
                  : { bgcolor: FP.color.surfaceAlt, color: FP.color.textSecondary, fontWeight: 600 }
              }
            />
          );
        },
      },
    ];

    if (mostrarAccionesListado) {
      cols.push({
        field: '__acciones',
        headerName: 'Acciones',
        flex: 0.38,
        minWidth: 104,
        sortable: false,
        filterable: false,
        disableExport: true,
        align: 'center',
        headerAlign: 'center',
        cellClassName: 'no-print',
        headerClassName: 'no-print',
        renderCell: (params) => (
          <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
            {puedeModificar && (
              <IconButton
                size="small"
                onClick={() => handleAbrirDialogo(params.row)}
                aria-label="Editar regla"
                sx={{ color: FP.color.accentBlue, '&:hover': { bgcolor: FP.color.accentBlueSoft } }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {puedeEliminar && (
              <IconButton
                size="small"
                onClick={() => handleEliminar(params.row)}
                aria-label="Eliminar regla"
                sx={{ color: FP.color.danger, '&:hover': { bgcolor: FP.color.dangerSoft } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        ),
      });
    }

    return cols;
  }, [mostrarAccionesListado, puedeModificar, puedeEliminar, handleAbrirDialogo, handleEliminar]);

  if (!selectedCompany?.db_empresa) {
    return (
      <Box component={embedded ? 'section' : 'main'} sx={embedded ? {} : { minHeight: '100vh', bgcolor: FP.color.surface }}>
        {!embedded && (
          <PageHeader title="Reglas de impuestos" subtitle="Seleccione una empresa activa" showQuickActions={false} />
        )}
        <Box sx={{ p: embedded ? 0 : { xs: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              maxWidth: 520,
              width: '100%',
              ...cardShellSx,
              p: { xs: 3, md: 4 },
              textAlign: 'center',
            }}
          >
            <DescriptionIcon sx={{ fontSize: 48, color: FP.color.textMuted, mb: 2 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 1 }}>
              Sin empresa seleccionada
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}>
              Elija una empresa en la barra superior para administrar reglas de impuestos sobre ventas.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeLeer) {
    return (
      <Box component={embedded ? 'section' : 'main'} sx={embedded ? {} : { minHeight: '100vh', bgcolor: FP.color.surface }}>
        {!embedded && (
          <PageHeader title="Reglas de impuestos" subtitle="Permisos · Acceso restringido" showQuickActions={false} />
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: embedded ? 0 : { xs: 2, sm: 3, md: 4 } }}>
          <Card
            elevation={0}
            sx={{
              maxWidth: 560,
              width: '100%',
              ...cardShellSx,
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso de lectura del programa «{programaPermiso}».
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  const subtitleHeader =
    nombreNegocio ? `${nombreNegocio} · Mantenimiento de productos` : 'Mantenimiento de productos';

  return (
    <Box
      component={embedded ? 'section' : 'main'}
      sx={embedded ? {} : { minHeight: '100vh', bgcolor: FP.color.surface }}
    >
      {!embedded && (
      <PageHeader
        title="Reglas de impuestos"
        subtitle={subtitleHeader}
        cta={
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => cargarReglasImpuestos()}
              disabled={cargando}
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                px: 2,
                height: 38,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleAbrirDialogo()}
              disabled={!puedeModificar}
              sx={primaryBtnSx}
            >
              Nueva regla
            </Button>
          </Box>
        }
      />
      )}

      <Box sx={{ p: embedded ? 0 : { xs: 1.5, sm: 2, md: 3 } }}>
        {reglasImpuestos.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total de reglas"
                value={String(reglasImpuestos.length)}
                icon={DescriptionIcon}
                accent={FP.color.accentBlue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Reglas activas"
                value={String(totalActivas)}
                icon={CheckCircleIcon}
                accent={FP.color.successDark}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Reglas inactivas"
                value={String(reglasImpuestos.length - totalActivas)}
                icon={BlockIcon}
                accent={FP.color.textSecondary}
              />
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Resultados
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {reglasImpuestos.length > 0 ? (
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <DataGrid
                  rows={reglasOrdenadas}
                  columns={columnasReglasImpuestos}
                  loading={cargando}
                  getRowId={(row) => String(row.id)}
                  slots={{ toolbar: ReglasImpuestosToolbar }}
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  disableRowSelectionOnClick
                  rowHeight={42}
                  sx={{ minWidth: { xs: 960, sm: '100%' }, height: 520, ...fpDataGridSx }}
                  autoHeight={false}
                />
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  {cargando ? 'Cargando…' : 'No hay datos para mostrar'}
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  {cargando
                    ? 'Espere mientras se obtienen las reglas de impuestos.'
                    : 'No hay reglas de impuestos configuradas. Use «Nueva regla» para crear una.'}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <Dialog
        open={dialogoAbierto}
        onClose={handleCerrarDialogo}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            px: 3,
            py: 2,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
            <Box sx={iconoTituloSx}>
              <AccountBalanceIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }}>
              {modoEdicion ? 'Editar regla de impuesto' : 'Nueva regla de impuesto'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCerrarDialogo}
            size="small"
            sx={{
              color: FP.color.textSecondary,
              border: `1px solid ${FP.color.border}`,
              borderRadius: 1.2,
              width: 32,
              height: 32,
              '&:hover': { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
            }}
            aria-label="Cerrar"
          >
            <ClearIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, overflow: 'visible', pt: 3 }}>
          <Grid container spacing={2.5} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre del impuesto"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
                sx={INPUT_SX}
                helperText="Nombre descriptivo (ej. ITBIS, IVA)"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Porcentaje"
                type="number"
                value={porcentaje}
                onChange={(e) => setPorcentaje(e.target.value)}
                required
                inputProps={{ min: 0, max: 100, step: 0.01 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                InputLabelProps={{ shrink: true }}
                sx={INPUT_SX}
                helperText="Entre 0 y 100"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required sx={INPUT_SX}>
                <InputLabel id="riv-tipodoc-label">Tipo de documento</InputLabel>
                <Select
                  labelId="riv-tipodoc-label"
                  multiple
                  value={Array.isArray(tipoDocumento) ? tipoDocumento : []}
                  onChange={(e) => setTipoDocumento(e.target.value)}
                  label="Tipo de documento"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} size="small" sx={chipDocSx} />
                      ))}
                    </Box>
                  )}
                >
                  {documentosAplicarImpuestos.length > 0 ? (
                    documentosAplicarImpuestos.map((doc) => (
                      <MenuItem key={doc} value={doc}>
                        {Array.isArray(tipoDocumento) && tipoDocumento.includes(doc) && '✓ '}
                        {doc}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem value="" disabled>
                      No hay documentos disponibles
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: FP.color.textSecondary }}>
                Documentos donde aplica esta regla
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={INPUT_SX}>
                <InputLabel>Aplicar sobre</InputLabel>
                <Select
                  value={aplicarSobre}
                  onChange={(e) => setAplicarSobre(e.target.value)}
                  label="Aplicar sobre"
                >
                  <MenuItem value="subtotal">Subtotal</MenuItem>
                  <MenuItem value="subtotal_menos_descuentos">Subtotal − descuentos</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: FP.color.textSecondary }}>
                {aplicarSobre === 'subtotal'
                  ? 'El impuesto se calcula sobre el subtotal.'
                  : 'El impuesto se calcula sobre el subtotal menos descuentos.'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth required sx={INPUT_SX}>
                <InputLabel>Tipo de operación</InputLabel>
                <Select value={tipoOperacion} onChange={(e) => setTipoOperacion(e.target.value)} label="Tipo de operación">
                  <MenuItem value="sumar">Sumar</MenuItem>
                  <MenuItem value="restar">Restar</MenuItem>
                </Select>
              </FormControl>
              <Typography variant="caption" sx={{ mt: 0.5, display: 'block', color: FP.color.textSecondary }}>
                {tipoOperacion === 'sumar' ? 'El impuesto se suma al total.' : 'El impuesto se resta del total.'}
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Monto base"
                type="number"
                value={montoBase}
                onChange={(e) => setMontoBase(e.target.value)}
                required
                inputProps={{ min: 0, step: 0.01 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                InputLabelProps={{ shrink: true }}
                sx={INPUT_SX}
                helperText="Monto mínimo de la venta para aplicar este impuesto"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Orden"
                type="number"
                value={orden}
                onChange={(e) => setOrden(e.target.value)}
                required
                inputProps={{ min: 0 }}
                InputLabelProps={{ shrink: true }}
                sx={INPUT_SX}
                helperText="Menor número = se aplica primero"
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={cuentasCatalogo}
                getOptionLabel={(option) => option.texto || option.codigo || ''}
                value={cuentaCatalogo}
                inputValue={busquedaCuenta}
                onInputChange={(e, newValue, reason) => {
                  setBusquedaCuenta(newValue);
                  if (reason === 'input') {
                    buscarCuentasCatalogo(newValue);
                  }
                }}
                onChange={(e, newValue) => {
                  setCuentaCatalogo(newValue);
                  if (!newValue) {
                    setBusquedaCuenta('');
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Catálogo de cuentas"
                    placeholder="Escriba para buscar…"
                    InputLabelProps={{ shrink: true }}
                    sx={INPUT_SX}
                    helperText="Cuenta contable asociada (opcional)"
                  />
                )}
                noOptionsText="No se encontraron cuentas"
                loadingText="Buscando cuentas…"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth sx={INPUT_SX}>
                <InputLabel>Estado</InputLabel>
                <Select value={activa} onChange={(e) => setActiva(e.target.value)} label="Estado">
                  <MenuItem value="S">Activa</MenuItem>
                  <MenuItem value="N">Inactiva</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${FP.color.border}`, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 0 }} />
          <Button variant="outlined" onClick={handleCerrarDialogo} sx={outlineBtnSx}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleGuardar}
            disabled={!puedeModificar}
            sx={primaryBtnSx}
          >
            {modoEdicion ? 'Modificar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default ReglasImpuestosVentas;
